import React, { useState, useEffect, useRef } from "react";
import { Input } from "@showitinc/showit-wc";
import Modal from "../../app2components/base/Modal/Modal";
import UserActions from "../../actions/UserActions";
import styles from "./CreateFolderModal.module.css";
import SiteFolderConstants from "@/constants/SiteFolderDefaultConstants";

const { SYSTEM_RESERVED_FOLDER_NAMES, CREATE_MODE, RENAME_MODE } = SiteFolderConstants;

const CreateFolderModal = ({
  isOpen,
  onClose,
  mode = CREATE_MODE,
  folderId = null,
  initialFolderName = "",
  existingFolders = [],
}) => {
  const [folderName, setFolderName] = useState("");
  const [validationError, setValidationError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setFolderName(mode === RENAME_MODE && initialFolderName ? initialFolderName : "");
      setValidationError("");

      // force input focus on render
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [isOpen, mode, initialFolderName]);

  /**
   * Handles the change of the folder name
   * @NOTE Folder name cannot be a system reserved name or empty from SiteFolderDefaultConstants.js
   * @param {Object} e - The event object
   * @returns {void}
   */
  const handleFolderNameChange = (e) => {
    const newName = e.target.value;
    setFolderName(newName);

    const validation = _validateFolderName(newName);
    setValidationError(validation.errorMessage);
  };

  const handleRename = () => {
    const validation = _validateFolderName(folderName);
    if (!validation.isValid) {
      setValidationError(validation.errorMessage);
      return;
    }

    const folderData = { name: folderName.trim() };
    UserActions.updateSiteDesignsFolder({ folderId, data: folderData });
    handleClose();
  };

  const handleCreate = () => {
    const validation = _validateFolderName(folderName);
    if (!validation.isValid) {
      setValidationError(validation.errorMessage);
      return;
    }

    const folderData = { name: folderName.trim() };
    UserActions.createSiteDesignsFolder(folderData);
    handleClose();
  };

  const handleSubmit = () => {
    if (!isFormValid) {
      return;
    }

    if (mode === RENAME_MODE) {
      handleRename();
    } else {
      handleCreate();
    }
  };

  /**
   * Checks if the folder name is a system reserved name. name.trim() is called internally to ensure the name is trimmed.
   * @param {string} name - The folder name to check
   * @returns {Object} - { isValid: boolean, errorMessage: string }
   */
  const _validateFolderName = (name) => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      return { isValid: false, errorMessage: "" };
    }

    if (SYSTEM_RESERVED_FOLDER_NAMES.includes(trimmedName)) {
      return { isValid: false, errorMessage: "Folder name cannot be a system reserved name" };
    }

    const duplicateFolder = existingFolders.find((folder) => {
      const isCurrentFolder = mode === "rename" && folder.name === initialFolderName;
      return !isCurrentFolder && folder.name.toLowerCase() === trimmedName.toLowerCase();
    });

    if (duplicateFolder) {
      return { isValid: false, errorMessage: "A folder with this name already exists" };
    }

    return { isValid: true, errorMessage: "" };
  };

  const handleClose = () => {
    setFolderName("");
    setValidationError("");
    onClose();
  };

  const isFormValid =
    folderName.trim() &&
    !validationError &&
    (mode !== RENAME_MODE || folderName.trim() !== initialFolderName);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={mode === RENAME_MODE ? "Rename Folder" : "Create New Folder"}
      size="small"
      secondaryButtonTitle="Cancel"
      secondaryButtonAction={handleClose}
      primaryButtonTitle={mode === RENAME_MODE ? "Rename" : "Create"}
      primaryButtonAction={mode === RENAME_MODE ? handleRename : handleCreate}
      primaryButtonDisabled={!isFormValid}
    >
      <div className={styles["modal-content"]}>
        <Input
          ref={inputRef}
          type="text"
          label="Folder Name"
          value={folderName}
          onSIInput={handleFolderNameChange}
          onSISubmit={handleSubmit}
          placeholder="Enter folder name"
        />
        {validationError && <div className={styles["validation-error"]}>{validationError}</div>}
      </div>
    </Modal>
  );
};

export default CreateFolderModal;
